import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import { Modal } from './Modal';
import type { PushSettings, OnboardingStatus } from '../types';
import './SettingsPage.css';

interface EditEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  onSave: (email: string) => Promise<void>;
}

interface EditNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  firstName: string;
  lastName: string;
  onSave: (firstName: string, lastName: string) => Promise<void>;
}

const EditNameModal: React.FC<EditNameModalProps> = ({
  isOpen,
  onClose,
  firstName,
  lastName,
  onSave,
}) => {
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFirst(firstName);
    setLast(lastName);
  }, [firstName, lastName, isOpen]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(first, last);
      onClose();
    } catch (error) {
      console.error('Failed to save name:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Изменение имени и фамилии">
      <div className="settings-edit-modal">
        <input
          type="text"
          className="settings-edit-modal-input"
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          placeholder="Имя"
        />
        <input
          type="text"
          className="settings-edit-modal-input"
          value={last}
          onChange={(e) => setLast(e.target.value)}
          placeholder="Фамилия"
        />
        <div className="settings-edit-modal-buttons">
          <button
            className="button--primary button--full"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            className="button--secondary button--full"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
    </Modal>
  );
};

const EditEmailModal: React.FC<EditEmailModalProps> = ({
  isOpen,
  onClose,
  currentEmail,
  onSave,
}) => {
  const [email, setEmail] = useState(currentEmail);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(currentEmail);
  }, [currentEmail, isOpen]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(email);
      onClose();
    } catch (error) {
      console.error('Failed to save email:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ваша почта">
      <div className="settings-edit-modal">
        <p className="settings-edit-modal-subtitle">
          Мы будем присылать на неё уведомления о призах и продажах
        </p>
        <input
          type="email"
          className="settings-edit-modal-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
        />
        <div className="settings-edit-modal-buttons">
          <button
            className="button--primary button--full"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            className="button--secondary button--full"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  const [pushSettings, setPushSettings] = useState<PushSettings | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);
  const [unlinkSBPModalOpen, setUnlinkSBPModalOpen] = useState(false);
  const [unlinkSBPLoading, setUnlinkSBPLoading] = useState(false);
  const [ownsPrizeNft, setOwnsPrizeNft] = useState(false);
  const [hasOwnedNfts, setHasOwnedNfts] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pushData, onboardingData, myNftsData] = await Promise.all([
        apiClient.getPushSettings().catch(() => null),
        apiClient.getOnboardingStatus(),
        apiClient.getMyNfts().catch(() => null),
      ]);
      setPushSettings(pushData);
      setOnboardingStatus(onboardingData);
      setOwnsPrizeNft(myNftsData?.nfts.some((n) => n.is_prize) ?? false);
      setHasOwnedNfts((myNftsData?.nfts.length ?? 0) > 0);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePush = async (type: string) => {
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
      
      setPushSettings((prev) => {
        const current = prev ?? {} as PushSettings;
        return {
          ...current,
          [type]: {
            ...current[type],
            enabled: !(current[type]?.enabled ?? true),
          },
        };
      });

      await apiClient.togglePushSetting(type);
    } catch (error) {
      console.error('Failed to toggle push setting:', error);
      // Revert on error
      loadData();
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    }
  };

  const handleLinkSBP = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    navigate('/sbp-linking');
  };

  const handleChangeSBP = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    navigate('/sbp-linking');
  };

  const handleUnlinkSBP = async () => {
    if (ownsPrizeNft) return;
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
      setUnlinkSBPLoading(true);
      await apiClient.unlinkSBP();
      setUnlinkSBPModalOpen(false);
      await loadData();
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Failed to unlink SBP:', error);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setUnlinkSBPLoading(false);
    }
  };

  const handleSaveEmail = async (email: string) => {
    await apiClient.updateProfile({ email });
    await refreshUser();

    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
    }
  };

  const handleSaveName = async (firstName: string, lastName: string) => {
    await apiClient.updateProfile({ first_name: firstName, last_name: lastName });
    await refreshUser();

    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
    }
  };

  const formatPhone = (phone: string): string => {
    // Format phone as +7 (XXX) XXX-XX-XX
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('7')) {
      return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9, 11)}`;
    }
    if (cleaned.length === 10) {
      return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8, 10)}`;
    }
    return phone;
  };

  if (loading) {
    return (
      <div className="page-container settings-page">
        <PageHeader title="Настройки" />
        <div className="settings-loading">Загрузка настроек...</div>
      </div>
    );
  }

  const sbpCompleted = onboardingStatus?.sbp_completed ?? false;
  const sbpPhone = onboardingStatus?.phone;
  const sbpBankName = onboardingStatus?.sbp_bank_name;

  // Map notification types to display info
  const notificationTypes = [
    {
      key: 'nft_sale',
      label: 'Продажа NFT',
      description: 'Отправим, когда ваш NFT будет продан',
    },
    {
      key: 'nft_prize',
      label: 'Получение призов',
      description: 'Отправим, если ваш NFT получит приз в рамках ивента',
    },
  ];

  return (
    <div className="page-container settings-page">
      <PageHeader title="Настройки" />

      {/* SBP Section */}
      <div className="settings-section">
        {sbpCompleted ? (
          <div className="settings-card sbp-card sbp-linked">
            <div className="sbp-linked-header">
              <img src="/success-s.png" alt="" className="sbp-linked-icon" />
              <span className="sbp-linked-title">СБП привязан</span>
            </div>
            <p className="sbp-linked-desc">Вы можете покупать и продавать NFT</p>
            <p className="sbp-linked-details">
              {[sbpPhone ? formatPhone(sbpPhone) : null, sbpBankName].filter(Boolean).join(' • ') || '—'}
            </p>
            <div className="sbp-linked-buttons">
              <button
                className="button--primary button--primary-s button--full"
                onClick={handleChangeSBP}
              >
                Изменить СБП
              </button>
              <button
                type="button"
                className="button--secondary button--secondary-s button--full"
                onClick={() => !ownsPrizeNft && setUnlinkSBPModalOpen(true)}
                disabled={ownsPrizeNft}
                aria-disabled={ownsPrizeNft}
              >
                Отвязать СБП
              </button>
              {ownsPrizeNft && (
                <p className="sbp-unlink-blocked-hint">
                  Отвязка недоступна, пока у вас есть призовая NFT
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="settings-card sbp-card sbp-not-linked">
            <div className="sbp-empty-title">СБП не привязан</div>
            <div className="sbp-empty-text">
              Привяжите СБП, чтобы покупать и продавать NFT
            </div>
            <button className="button--accent button--accent-s button--full" onClick={handleLinkSBP}>
              Привязать СБП
            </button>
          </div>
        )}
      </div>

      {/* Personal Data Section */}
      <div className="settings-section">
        <h2 className="settings-section-title">Личные данные</h2>
        <div className="settings-card settings-profile-card">
          <div className="profile-data-list">
            <div className="profile-data-item profile-data-item--readonly">
              <span className="profile-data-label">Username</span>
              <span className="profile-data-value">
                {user?.username ? `@${user.username}` : '—'}
              </span>
            </div>
            <button
              className="profile-data-item profile-data-item--clickable"
              onClick={() => setEditNameModalOpen(true)}
            >
              <span className="profile-data-label">Имя и фамилия</span>
              <span className="profile-data-value-wrap">
                <span className="profile-data-value">
                  {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Не указано'}
                </span>
                <img src="/chevron.png" alt="" className="profile-data-arrow" />
              </span>
            </button>
            <button
              className="profile-data-item profile-data-item--clickable"
              onClick={() => setEditModalOpen(true)}
            >
              <span className="profile-data-label">Email</span>
              <span className="profile-data-value-wrap">
                <span className="profile-data-value">
                  {user?.email || 'Не указан'}
                </span>
                <img src="/chevron.png" alt="" className="profile-data-arrow" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="settings-section">
        <h2 className="settings-section-title">Уведомления</h2>
        <div className="settings-card">
          {notificationTypes.map((notif) => {
            const setting = pushSettings?.[notif.key];
            const isEnabled = setting?.enabled ?? true;

            return (
              <div key={notif.key} className="notification-item">
                <div className="notification-info">
                  <div className="notification-text">
                    <span className="notification-label">{notif.label}</span>
                    <span className="notification-description">{notif.description}</span>
                  </div>
                </div>
                <button
                  className={`toggle-button ${isEnabled ? 'active' : ''}`}
                  onClick={() => handleTogglePush(notif.key)}
                >
                  <span className="toggle-slider" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Name Modal */}
      <EditNameModal
        isOpen={editNameModalOpen}
        onClose={() => setEditNameModalOpen(false)}
        firstName={user?.first_name || ''}
        lastName={user?.last_name || ''}
        onSave={handleSaveName}
      />

      {/* Edit Email Modal */}
      <EditEmailModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        currentEmail={user?.email || ''}
        onSave={handleSaveEmail}
      />

      {/* Unlink SBP Confirmation Modal */}
      <Modal
        isOpen={unlinkSBPModalOpen}
        onClose={() => setUnlinkSBPModalOpen(false)}
        title="Отвязать СБП?"
      >
        <div className="settings-edit-modal">
          {hasOwnedNfts ? (
            <>
              <p className="unlink-sbp-modal-text">
                Все ваши NFT будут заморожены на 24 часа и снова станут активными после повторной
                привязки СБП. Если через 24 часа СБП так и не будет привязан, токены будут
                распределены между участниками в случайном порядке.
              </p>
              <p className="unlink-sbp-modal-text unlink-sbp-modal-text--secondary">
                Пока NFT заморожены, они не отображаются в общем каталоге и их нельзя купить.
              </p>
            </>
          ) : (
            <p className="unlink-sbp-modal-text">
              Без привязанного СБП совершать покупки NFT будет невозможно.
            </p>
          )}
          <div className="settings-edit-modal-buttons">
            <button
              className="button--primary button--full"
              onClick={handleUnlinkSBP}
              disabled={unlinkSBPLoading}
            >
              {unlinkSBPLoading ? 'Отвязка...' : 'Да, отвязать'}
            </button>
            <button
              className="button--secondary button--full"
              onClick={() => setUnlinkSBPModalOpen(false)}
            >
              Отмена
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
